import React from "react";
import Reveal from "./ui/Reveal";
import SectionLabel from "./ui/SectionLabel";
import useParallax from "../hooks/useParallax";
import useTilt from "../hooks/useTilt";

const BENEFITS = [
  {
    tone: "teal",
    title: "Ficha veterinária & linha do tempo",
    text: "O tutor acompanha o histórico clínico completo numa linha do tempo visual de vacinas e tratamentos.",
    d: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z",
  },
  {
    tone: "amber",
    title: "Alertas de saúde automáticos",
    text: "Nunca perca um prazo. Lembretes de vacinas, exames e medicações saem sozinhos — e voltam como agendamento.",
    d: "M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 00-4-5.7V5a2 2 0 10-4 0v.3C7.7 6.2 6 8.4 6 11v3.2c0 .5-.2 1-.6 1.4L4 17h5m6 0v1a3 3 0 11-6 0v-1",
  },
  {
    tone: "teal",
    title: "A sua marca, não a nossa",
    text: "Logo, cores e nome do seu pet shop no app que o cliente abre. O valor percebido do serviço sobe junto.",
    d: "M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4l2 2h6a2 2 0 012 2v10a4 4 0 01-4 4H7z",
  },
];

const TONES = {
  teal: "bg-tealVitality/10 text-tealVitality group-hover:bg-tealVitality group-hover:text-white",
  amber: "bg-pawOrange/10 text-pawOrange group-hover:bg-pawOrange group-hover:text-white",
};

const Customization = () => {
  const parallaxRef = useParallax(0.14);
  const tilt = useTilt({ max: 6, scale: 1.02 });

  return (
    <section
      id="customization"
      className="relative overflow-hidden bg-white py-28"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[500px] w-[500px] rounded-full bg-tealVitality/[.08] blur-[130px]" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-pawOrange/[.09] blur-[120px]" />

      <div className="relative mx-auto max-w-7xl px-6">
        <div className="grid items-center gap-16 lg:grid-cols-2 lg:gap-20">
          {/* ---------- Visual ---------- */}
          <Reveal
            variant="left"
            className="relative order-2 lg:order-1"
          >
            <div ref={parallaxRef}>
              <div
                className="tilt-scene relative rounded-[44px] bg-gradient-to-br from-pawOrange/12 via-accentWarm to-tealVitality/10 p-8 md:p-14"
                onMouseMove={tilt.onMouseMove}
                onMouseLeave={tilt.onMouseLeave}
              >
                {/* Patinha marca d'água */}
                <svg
                  className="pointer-events-none absolute -bottom-6 -left-6 h-40 w-40 text-midnight/[.04]"
                  viewBox="0 0 64 64"
                  fill="currentColor"
                >
                  <ellipse cx="20" cy="20" rx="7.5" ry="10" transform="rotate(-18 20 20)" />
                  <ellipse cx="34" cy="14" rx="7" ry="10.5" />
                  <ellipse cx="47" cy="20" rx="7" ry="9.5" transform="rotate(16 47 20)" />
                  <path d="M34 28c9 0 17 7.4 17 15.4 0 6.6-5 10.6-11.4 10.6-3 0-4.4-1.2-7-1.2s-4 1.2-7 1.2C19.2 54 14 50 14 43.4 14 35.4 25 28 34 28Z" />
                </svg>

                <div ref={tilt.ref} className="tilt-target relative">
                  <img
                    src="/tela_pets.png"
                    alt="Fichas de pets no aplicativo do tutor"
                    loading="lazy"
                    className="mx-auto w-full rounded-2xl shadow-[0_35px_70px_-25px_rgba(15,31,61,.45)]"
                  />
                </div>

                {/* Estatística flutuante */}
                <div className="glass absolute -right-4 top-14 animate-float-soft rounded-2xl px-5 py-4 shadow-lift md:-right-10">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-midnight/40">
                    Vacinas em dia
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="font-display text-3xl font-extrabold text-tealVitality">
                      98%
                    </span>
                    <svg
                      className="h-5 w-5 text-emerald-500"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                      />
                    </svg>
                  </div>
                </div>

                {/* Notificação flutuante */}
                <div
                  className="glass absolute -left-4 bottom-10 animate-float-soft rounded-2xl px-4 py-3 shadow-lift md:-left-12"
                  style={{ animationDelay: "1.4s" }}
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-pawOrange/15 text-pawOrange">
                      <svg
                        className="h-4 w-4"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    <p className="text-xs font-semibold leading-tight text-midnight/75">
                      V10 da Lua
                      <br />
                      <span className="font-medium text-midnight/45">
                        vence em 12 dias
                      </span>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* ---------- Texto ---------- */}
          <div className="order-1 lg:order-2">
            <Reveal>
              <SectionLabel tone="teal">Personalização</SectionLabel>
            </Reveal>

            <Reveal delay={90}>
              <h2 className="mb-6 mt-6 font-display text-[clamp(2rem,4.2vw,3.1rem)] font-extrabold leading-[1.08] text-midnight">
                A saúde do pet na
                <br className="hidden md:block" />{" "}
                <span className="underline-sketch">palma da mão</span>
              </h2>
            </Reveal>

            <Reveal delay={170}>
              <p className="mb-10 text-lg leading-relaxed text-midnight/55">
                Um app exclusivo com a sua marca para os tutores acompanharem a
                vida do pet. Mais retenção, menos ligação perguntando "como ele
                está?".
              </p>
            </Reveal>

            <div className="space-y-3">
              {BENEFITS.map((benefit, i) => (
                <Reveal key={benefit.title} delay={240 + i * 100}>
                  <div className="group flex gap-5 rounded-2xl p-4 transition-colors duration-300 hover:bg-softIvory">
                    <div
                      className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-all duration-500 group-hover:-rotate-6 group-hover:scale-110 ${
                        TONES[benefit.tone]
                      }`}
                    >
                      <svg
                        className="h-6 w-6"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d={benefit.d}
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="mb-1 font-display text-lg font-bold text-midnight">
                        {benefit.title}
                      </h4>
                      <p className="leading-relaxed text-midnight/55">
                        {benefit.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Customization;
